import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { CdkPortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { MatListItem } from '@angular/material/list';
import { MatTooltip } from '@angular/material/tooltip';
import { MenuGroupWithMenuItems } from '@core/menu/store/selectors';
import { MenuItemDto } from '@core/services/api-clients';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { MenuGroupItemsComponent } from '../menu-group-items/menu-group-items.component';
import { MenuItemComponent } from '../menu-item/menu-item.component';
import { MenuPopupComponent } from '../menu-popup/menu-popup.component';

export class SelectedMenuGroup {
  menuGroupId: string;
  menuAreaId: string;
  isExpandable: boolean;
  isExpanded: boolean;
  isSpecial: boolean;
}

@Component({
  selector: 'app-menu-group',
  templateUrl: './menu-group.component.html',
  styleUrls: ['./menu-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatListItem,
    MatTooltip,
    MenuItemComponent,
    MenuGroupItemsComponent,
    CdkPortal,
    MenuPopupComponent,
  ],
})
export class MenuGroupComponent implements OnInit, OnDestroy {
  private _overlay = inject(Overlay);
  private ref = inject(ElementRef);

  readonly menuGroup = input<MenuGroupWithMenuItems>(undefined);
  readonly selectedMenuItemId = input<string>(undefined);
  readonly isSelected = input<boolean>(false);
  readonly isWide = input<boolean>(false);
  readonly isSpecial = input<boolean>(false);
  readonly isPopupEnabled = input<boolean>(false);
  readonly isFavoriteEnabled = input<boolean>(false);
  readonly favorites = input<{
    [id: string]: boolean;
  }>(null);

  readonly menuItemClick = output<MenuItemDto>();
  readonly menuItemAddedToFavorite = output<MenuItemDto>();
  readonly menuItemRemovedFromFavorite = output<MenuItemDto>();
  readonly menuGroupClick = output<SelectedMenuGroup>();

  readonly menuPopupContent = viewChild<CdkPortal>('popupContent');

  get isVisible(): boolean {
    const menuGroup = this.menuGroup();
    return menuGroup && (!!menuGroup.menuItems?.length || this.isSpecial());
  }
  get isExpandable(): boolean {
    return this.menuGroup()?.menuItems?.length > 1 || this.isSpecial();
  }

  private overlayRef: OverlayRef = null;
  private mouseOverPopup: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private mouseOverSource: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private mouseOverPopup$ = this.mouseOverPopup.asObservable();
  private mouseOverSource$ = this.mouseOverSource.asObservable();
  private subscriptions = new Subscription();

  constructor() {}

  ngOnInit(): void {
    this.subscriptions.add(
      combineLatest([this.mouseOverSource$, this.mouseOverPopup$])
        .pipe(
          debounceTime(300),
          tap(([isOverSource, isOverPopup]) => {
            if (isOverSource && !this.isSelected() && this.isExpandable) {
              this.openGroupItemsPopup();
              return;
            }

            if (!isOverSource && !isOverPopup) {
              this.closeGroupItemsPopup();
              return;
            }
          }),
        )
        .subscribe(),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.closeGroupItemsPopup();
  }

  onMenuGroupClick() {
    this.closeGroupItemsPopup();
    this.menuGroupClick.emit({
      menuGroupId: this.menuGroup().id,
      menuAreaId: null,
      isExpandable: this.isExpandable,
      isExpanded: this.isSelected(),
      isSpecial: this.isSpecial(),
    });
  }

  onMouseOver() {
    if (this.mouseOverSource.value != true) this.mouseOverSource.next(true);
  }

  onMouseOut() {
    if (this.mouseOverSource.value != false) this.mouseOverSource.next(false);
  }

  onMouseOverOverlay(isOver: boolean) {
    if (this.mouseOverPopup.value != isOver) this.mouseOverPopup.next(isOver);
  }

  onMenuItemClick(menuItem: MenuItemDto) {
    this.menuItemClick.emit(menuItem);
  }

  addMenuItemToFavorite(menuItem: MenuItemDto) {
    this.menuItemAddedToFavorite.emit(menuItem);
  }

  removeMenuItemFromFavorite(menuItem: MenuItemDto) {
    this.menuItemRemovedFromFavorite.emit(menuItem);
  }

  private openGroupItemsPopup() {
    if (this.overlayRef || !this.isPopupEnabled()) return;
    const overlayConfig = new OverlayConfig({
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this.ref.nativeElement)
        .withPositions([
          {
            originX: 'end',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),
      scrollStrategy: this._overlay.scrollStrategies.close(),
    });
    this.overlayRef = this._overlay.create(overlayConfig);
    this.overlayRef.attach(this.menuPopupContent());
  }

  private closeGroupItemsPopup() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }
}
